import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-token-reveal.css?inline';
import '../pk-alert/pk-alert.js';

/**
 * `<pk-token-reveal>` — one-time reveal surface for a freshly-minted secret
 * (API token, webhook URL, password reset link).
 *
 * Different from `<pk-token-panel>` — the panel manages a token *list* with
 * mint/revoke and an internal modal-reveal. This component is the standalone
 * "save this now, we won't show it again" surface used in onboarding flows,
 * admin password-reset flows, and webhook generation.
 *
 * Visual: dark monospace code block + Show/Hide toggle + Copy button +
 * yellow warning callout above. The dark code block is intentional — it's a
 * terminal-style aesthetic that signals "this is a secret, treat it like one".
 *
 * Attributes:
 *   - `token`    (required) — the secret value to display
 *   - `label`    — small uppercase heading above the warning. Default: `Your API token`
 *   - `warning`  — body of the warning alert. Default: `Shown once only — save it now.`
 *   - `masked`   — boolean attribute; when present, starts in masked state
 *                   (user must click Show to reveal). Defaults to revealed.
 *
 * Events:
 *   - `pk-token-copied` — fires on successful copy. detail: `{ token }`.
 *
 * Methods:
 *   - `copy()` — programmatic copy without click (returns Promise<boolean>)
 */
export class PkTokenReveal extends HTMLElement {
    static readonly tagName = 'pk-token-reveal';

    static get observedAttributes(): string[] {
        return ['token', 'label', 'warning', 'masked'];
    }

    private readonly _root: ShadowRoot;
    private _revealed = true;
    private _justCopied = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._revealed = !this.hasAttribute('masked');
        this._render();
    }

    attributeChangedCallback(name: string, oldVal: string | null, newVal: string | null): void {
        if (!this.isConnected) return;
        if (oldVal === newVal) return;
        if (name === 'masked') {
            this._revealed = newVal === null;
        }
        this._render();
    }

    private get _token(): string {
        return this.getAttribute('token') ?? '';
    }

    private get _label(): string {
        return this.getAttribute('label') ?? 'Your API token';
    }

    private get _warning(): string {
        return this.getAttribute('warning') ?? 'Shown once only — save it now.';
    }

    public async copy(): Promise<boolean> {
        const value = this._token;
        if (!value) return false;
        try {
            await navigator.clipboard.writeText(value);
            this._justCopied = true;
            this._render();
            setTimeout(() => {
                this._justCopied = false;
                if (this.isConnected) this._render();
            }, 2000);
            this.dispatchEvent(new CustomEvent('pk-token-copied', {
                bubbles: true,
                composed: true,
                detail: { token: value },
            }));
            return true;
        } catch {
            return false;
        }
    }

    private readonly _onToggle = (): void => {
        this._revealed = !this._revealed;
        this._render();
    };

    private readonly _onCopy = (): void => {
        void this.copy();
    };

    private template(): TemplateResult {
        const token     = this._token;
        const revealed  = this._revealed;
        const justCopied = this._justCopied;
        const display   = revealed ? token : '•'.repeat(Math.min(token.length, 24));

        return html`
            <style>${styles}</style>

            <p class="label">${this._label}</p>

            <pk-alert variant="warn" class="warning">${this._warning}</pk-alert>

            <div class="token-wrap">
                <div
                    class="token-box ${revealed ? '' : 'masked'}"
                    role="textbox"
                    aria-readonly="true"
                    aria-label="Secret value">${display}</div>
                <button
                    class="btn btn-toggle"
                    type="button"
                    aria-pressed=${revealed ? 'true' : 'false'}
                    aria-label=${revealed ? 'Hide secret' : 'Show secret'}
                    @click=${this._onToggle}
                >${revealed ? 'Hide' : 'Show'}</button>
                <button
                    class="btn btn-copy"
                    type="button"
                    aria-label="Copy secret to clipboard"
                    @click=${this._onCopy}
                >${justCopied ? 'Copied!' : 'Copy'}</button>
            </div>
        `;
    }

    private _render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkTokenReveal.tagName)) {
    customElements.define(PkTokenReveal.tagName, PkTokenReveal);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-token-reveal': PkTokenReveal;
    }
}
